import { useState, useEffect, useCallback } from 'react';
import { fetchOperations } from './requester';
import { Operation, OperationsResponse } from './types';
import { mergeOperations } from '../../utils/operations';
import { updateSelectedOperationsFromSessionStorage } from '../../utils/operationsInSessionStorage';
import { ALL_OPERATIONS, OperationStorage } from '../../constants/operations';
import { setOperationStorageItem } from '../../utils/operationsStoragePersistence';

export const useOperations = (userId: string): {
  isLoading: boolean,
  operations: Operation[],
  operationsSelected: Operation[]
  setSelectedOperations: (operationSelected: Operation[]) => void,
} => {
  const [operations, setOperations] = useState<Operation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [operationsSelected, setOperationsSelected] = useState<Operation[]>([]);
  const removeAllOperationsItem = useCallback(({ operationId }: { operationId: string }) => operationId !== ALL_OPERATIONS, []);


  const getOperations = useCallback(async () => {
    if (userId) {
      try {
        const operationsResponse: OperationsResponse = await fetchOperations(userId);
        const mergeResponse = mergeOperations(operationsResponse);
        setOperations(mergeResponse);
        updateSelectedOperationsFromSessionStorage(mergeResponse, setOperationsSelected);
        setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    }
  }, [userId]);

  useEffect(() => {
    getOperations();
  }, [getOperations]);

  const handleAllOperationsSelection = useCallback((userHasOneOperation: boolean, allOperationsWithOutAllId: Operation[]) => {
    if (!userHasOneOperation) {
      const allOperationIds = allOperationsWithOutAllId.map(({ operationId }) => operationId);
      setOperationStorageItem(OperationStorage.OperationsSelected, JSON.stringify(allOperationIds));
      setOperationStorageItem(OperationStorage.OperationSelected, ALL_OPERATIONS);
      setOperationsSelected(allOperationsWithOutAllId);
    } else {
      setOperationStorageItem(OperationStorage.OperationSelected, operations[1].operationId);
      setOperationStorageItem(OperationStorage.OperationsSelected, JSON.stringify([operations[1].operationId]));
      setOperationsSelected([operations[1]]);
    }
  }, [operations]);

  const handleNormalSelection = useCallback((selected: Operation[]) => {
    const getOperationsId = selected.map(({ operationId }) => operationId);
    setOperationStorageItem(OperationStorage.OperationSelected, getOperationsId.length ? getOperationsId[0] : operations[0].operationId);
    setOperationStorageItem(OperationStorage.OperationsSelected, JSON.stringify(getOperationsId));
    setOperationsSelected(selected);
  }, [operations]);

  const setSelectedOperations = useCallback((selected: Operation[]) => {
    if (!Array.isArray(selected) || !selected[0]) return;

    const allOperationsObjectSelected = selected[0].operationId === ALL_OPERATIONS;
    const allOperationsWithOutAllId = operations.filter(removeAllOperationsItem);
    const allOperationsAreSelected = selected.length === allOperationsWithOutAllId.length;
    const userHasOneOperation = operations.length === 2;

    if (allOperationsObjectSelected || allOperationsAreSelected) {
      handleAllOperationsSelection(userHasOneOperation, allOperationsWithOutAllId);
    } else {
      handleNormalSelection(selected);
    }
  }, [operations, removeAllOperationsItem, handleAllOperationsSelection, handleNormalSelection]);

  return {
    isLoading,
    operations,
    operationsSelected,
    setSelectedOperations
  };
};
